import { useMemo } from 'react';
import { Nullable, Product, ProductBatch } from '@framework/types';

export function formatPrice({
  amount,
  currencyCode,
  locale,
}: {
  amount: number;
  currencyCode: string;
  locale: string;
}) {
  const formatCurrency = new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: currencyCode,
  });

  return formatCurrency.format(amount);
}

export function formatVariantPrice({
  amount,
  baseAmount,
  currencyCode,
  locale,
}: {
  baseAmount: number;
  amount: number;
  currencyCode: string;
  locale: string;
}) {
  const hasDiscount = baseAmount > amount;
  const formatDiscount = new Intl.NumberFormat(locale, { style: 'percent' });
  const discount = hasDiscount
    ? formatDiscount.format((baseAmount - amount) / baseAmount)
    : null;

  const price = formatPrice({ amount, currencyCode, locale });
  const basePrice = hasDiscount
    ? formatPrice({ amount: baseAmount, currencyCode, locale })
    : null;

  return { price, basePrice, discount };
}

export const getPriceForProduct = (
  product: Nullable<Product>,
  selectedBatch: Nullable<ProductBatch>,
) => {
  if (!product) return 0;
  return selectedBatch?.sale_percentage
    ? product.price * (1 - selectedBatch.sale_percentage / 100)
    : (product.sale_price ?? product.price);
};

export const useGetPrice = (
  product: Nullable<Product>,
  selectedBatch: Nullable<ProductBatch>,
  currencyCode: string,
) => {
  if (!product) return usePrice();

  return usePrice({
    amount: getPriceForProduct(product, selectedBatch),
    baseAmount: product.price,
    currencyCode,
  });
};

export default function usePrice(
  data?: {
    amount: number;
    baseAmount?: number;
    currencyCode: string;
  } | null,
) {
  const { amount, baseAmount, currencyCode } = data ?? {};
  const locale = 'en';
  const value = useMemo(() => {
    if (typeof amount !== 'number' || !currencyCode) return '';

    return baseAmount
      ? formatVariantPrice({ amount, baseAmount, currencyCode, locale })
      : formatPrice({ amount, currencyCode, locale });
  }, [amount, baseAmount, currencyCode]);

  return typeof value === 'string'
    ? { price: value, basePrice: null, discount: null, priceAsNumber: amount }
    : value;
}
